import { useDarkMode } from "../ThemeContext";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import quotesData from "../data/quotes.json";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";


const LAST_READ_QUOTE_KEY = "brahm-ratna-last-read";

function writeCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const pair = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastReadQuote(quote) {
  const payload = JSON.stringify({
    number: quote.number,
    guru: quote.guru,
  });
  try {
    localStorage.setItem(LAST_READ_QUOTE_KEY, payload);
  } catch {
    // no-op
  }
  writeCookie(LAST_READ_QUOTE_KEY, payload);
}

function loadLastReadQuote() {
  let raw = "";
  try {
    raw = localStorage.getItem(LAST_READ_QUOTE_KEY) || "";
  } catch {
    // no-op
  }
  if (!raw) raw = readCookie(LAST_READ_QUOTE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.number) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getSnippet(text, word) {
  const idx = text.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return text.slice(0, 120).trim() + "\u2026";
  const start = Math.max(0, idx - 60);
  const end = Math.min(text.length, idx + word.length + 60);
  let s = text.slice(start, end).trim();
  if (start > 0) s = "\u2026" + s;
  if (end < text.length) s += "\u2026";
  return s;
}

function getSnippetForCandidates(content, candidates) {
  const lower = content.toLowerCase();
  let best = null;

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const idx = lower.indexOf(candidate.toLowerCase());
    if (idx === -1) return;
    if (!best || idx < best.idx) {
      best = { idx, candidate };
    }
  });

  if (!best) return { snippet: "", hit: "" };
  return {
    snippet: getSnippet(content, best.candidate),
    hit: best.candidate,
  };
}

function Highlighted({ text, word }) {
  if (!(word || "").trim()) return <>{text}</>;
  const regex = new RegExp(
    `(${(word || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

// Two categories keyed by substring match on the guru field
const CATEGORIES = [
  {
    id: "hariprasad",
    label: "ગુરૂહરિ હરિપ્રસાદ સ્વામીજી",
    match: (guru) =>
      guru.includes("હરિપ્રસાદ") || guru.toLowerCase().includes("hariprasad"),
  },
  {
    id: "prabodh",
    label: "ગુરૂહરિ પ્રબોધજીવન સ્વામીજી",
    match: (guru) =>
      guru.includes("પ્રબોધ") || guru.toLowerCase().includes("prabodh"),
  },
];

const ROMAN_WORD_OVERRIDES = {
  gunatit: "ગુણાતીત",
  gunatīt: "ગુણાતીત",
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
  "~": "ળ",
  "^": "ટ",
};

const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};

const GUJARATI_PHONETIC_EQUIVALENTS = [
  [/િ|ી/g, "િ"],
  [/ુ|ૂ/g, "ુ"],
  [/ે|ૈ/g, "ે"],
  [/ો|ૌ/g, "ો"],
  [/ઁ|ં|ઃ/g, "ં"],
  [/શ|ષ|સ/g, "સ"],
  [/ળ/g, "લ"],
];

const GUJARATI_INDEPENDENT_VOWELS = {
  અ: "a",
  આ: "aa",
  ઇ: "i",
  ઈ: "i",
  ઉ: "u",
  ઊ: "u",
  ઋ: "ri",
  એ: "e",
  ઐ: "ai",
  ઓ: "o",
  ઔ: "au",
};

const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

const GUJARATI_CONSONANTS = {
  ક: "k",
  ખ: "kh",
  ગ: "g",
  ઘ: "gh",
  ઙ: "ng",
  ચ: "ch",
  છ: "chh",
  જ: "j",
  ઝ: "jh",
  ઞ: "ny",
  ટ: "t",
  ઠ: "th",
  ડ: "d",
  ઢ: "dh",
  ણ: "n",
  ત: "t",
  થ: "th",
  દ: "d",
  ધ: "dh",
  ન: "n",
  પ: "p",
  ફ: "ph",
  બ: "b",
  ભ: "bh",
  મ: "m",
  ય: "y",
  ર: "r",
  લ: "l",
  વ: "v",
  શ: "sh",
  ષ: "sh",
  સ: "s",
  હ: "h",
  ળ: "l",
};

const GUJARATI_SIGNS = {
  "ં": "m",
  "ઃ": "h",
  "ઁ": "m",
};

const GUJARATI_VIRAMA = "્";

function isGujaratiText(text) {
  return /[\u0A80-\u0AFF]/.test(text || "");
}

function normalizeGujaratiPhonetic(text) {
  let normalized = (text || "").toLowerCase().replace(/\s+/g, "");
  GUJARATI_PHONETIC_EQUIVALENTS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

function normalizeRomanPhonetic(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function gujaratiToRomanPhonetic(text) {
  const input = text || "";
  let out = "";

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1] || "";

    if (c === "ક" && next === "્" && input[i + 2] === "ષ") {
      out += "ksh";
      i += 2;
      continue;
    }
    if (c === "જ" && next === "્" && input[i + 2] === "ઞ") {
      out += "gn";
      i += 2;
      continue;
    }

    if (GUJARATI_INDEPENDENT_VOWELS[c]) {
      out += GUJARATI_INDEPENDENT_VOWELS[c];
      continue;
    }

    if (GUJARATI_CONSONANTS[c]) {
      const base = GUJARATI_CONSONANTS[c];
      const nextChar = input[i + 1] || "";
      if (GUJARATI_MATRAS[nextChar]) {
        out += base + GUJARATI_MATRAS[nextChar];
        i += 1;
        continue;
      }
      if (nextChar === GUJARATI_VIRAMA) {
        out += base;
        i += 1;
        continue;
      }
      out += base + "a";
      continue;
    }

    if (GUJARATI_SIGNS[c]) {
      out += GUJARATI_SIGNS[c];
      continue;
    }

    out += " ";
  }

  out = out.replace(/a(\s|$)/g, "$1");
  return normalizeRomanPhonetic(out);
}

function transliterateRomanWordToGujarati(word) {
  const override = ROMAN_WORD_OVERRIDES[(word || "").toLowerCase()];
  if (override) return override;

  const input = (word || "")
    .replace(/L/g, "~")
    .replace(/T/g, "^")
    .toLowerCase()
    .replace(/[^a-z~^]/g, "");
  if (!input) return "";

  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );

  let out = "";
  let i = 0;
  while (i < input.length) {
    const remaining = input.slice(i);

    let vowelToken = "";
    for (const v of vowels) {
      if (remaining.startsWith(v)) {
        vowelToken = v;
        break;
      }
    }
    if (vowelToken) {
      out += ROMAN_VOWELS[vowelToken].independent;
      i += vowelToken.length;
      continue;
    }

    let consonantToken = "";
    for (const c of consonants) {
      if (remaining.startsWith(c)) {
        consonantToken = c;
        break;
      }
    }
    if (!consonantToken) {
      i += 1;
      continue;
    }

    out += ROMAN_CONSONANTS[consonantToken];
    i += consonantToken.length;

    const afterConsonant = input.slice(i);
    let followingVowel = "";
    for (const v of vowels) {
      if (afterConsonant.startsWith(v)) {
        followingVowel = v;
        break;
      }
    }
    if (followingVowel) {
      out += ROMAN_VOWELS[followingVowel].matra;
      i += followingVowel.length;
    }
  }

  return out;
}

function generateGujaratiLVariants(text) {
  const source = text || "";
  if (!/[લળ]/.test(source)) return [source];

  let variants = [""];
  for (const ch of source) {
    if (ch === "લ" || ch === "ળ") {
      const next = [];
      variants.forEach((prefix) => {
        next.push(prefix + "લ");
        next.push(prefix + "ળ");
      });
      variants = next;
      continue;
    }
    variants = variants.map((prefix) => prefix + ch);
  }
  return Array.from(new Set(variants));
}

function buildWordCandidates(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const candidates = new Set([q]);
  const lowered = q.toLowerCase();

  if (!isGujaratiText(q) && /^[a-z\s]+$/i.test(q)) {
    const transliterated = q
      .split(/\s+/)
      .map((w) => transliterateRomanWordToGujarati(w))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (transliterated) {
      generateGujaratiLVariants(transliterated).forEach((variant) => {
        candidates.add(variant);
        candidates.add(normalizeGujaratiPhonetic(variant));
      });
    }
    if (ROMAN_WORD_OVERRIDES[lowered]) {
      candidates.add(ROMAN_WORD_OVERRIDES[lowered]);
      candidates.add(normalizeGujaratiPhonetic(ROMAN_WORD_OVERRIDES[lowered]));
    }
  }

  if (isGujaratiText(q)) {
    candidates.add(normalizeGujaratiPhonetic(q));
  }

  return Array.from(candidates).filter(Boolean);
}

export default function BrahmRatna() {
  const [category, setCategory] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [fontSize, setFontSize] = useState(1);

  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState(null);
  const [resultHighlightWord, setResultHighlightWord] = useState("");
  const [lastReadQuote, setLastReadQuote] = useState(() => loadLastReadQuote());
  const [darkMode, setDarkMode] = useDarkMode();

  const handleWordSearch = (e) => {
    e.preventDefault();
    const q = wordQuery.trim();
    if (!q) return;
    const ql = q.toLowerCase();
    const queryRomanNormalized = normalizeRomanPhonetic(q);
    const queryIsRoman = !isGujaratiText(q) && /^[a-z\s]+$/i.test(q);
    const candidates = buildWordCandidates(q);
    const normalizedCandidates = candidates.map((c) =>
      normalizeGujaratiPhonetic(c),
    );
    const results = quotesData.reduce((acc, item) => {
      const content = `${item.quote || ""} ${item.guru || ""} ${item.place || ""} ${item.date || ""}`;
      const contentLower = content.toLowerCase();
      const contentPhonetic = normalizeGujaratiPhonetic(content);
      if (!item._romanPhonetic) {
        item._romanPhonetic = gujaratiToRomanPhonetic(content);
      }

      let matched = false;
      for (let i = 0; i < candidates.length; i += 1) {
        const c = candidates[i];
        const normalized = normalizedCandidates[i];
        if (
          (c && contentLower.includes(c.toLowerCase())) ||
          (normalized && contentPhonetic.includes(normalized))
        ) {
          matched = true;
          break;
        }
      }

      if (
        !matched &&
        queryIsRoman &&
        queryRomanNormalized &&
        item._romanPhonetic.includes(queryRomanNormalized)
      ) {
        matched = true;
      }

      if (!matched && !contentLower.includes(ql)) return acc;

      const { snippet, hit } = getSnippetForCandidates(content, [
        ...candidates,
        q,
      ]);
      acc.push({ item, snippet, hit: hit || q });
      return acc;
    }, []);
    setWordResults(results);
    setResultHighlightWord(results[0]?.hit || q);
  };

  const clearWordSearch = () => {
    setWordQuery("");
    setWordResults(null);
    setResultHighlightWord("");
  };

  const loadQuote = (quote) => {
    setSelected(quote);
    setLastReadQuote({ number: quote.number, guru: quote.guru });
    saveLastReadQuote(quote);
  };

  const handleResultClick = (quote) => {
    loadQuote(quote);
  };

  const filtered = quotesData.filter((q) => {
    if (category) {
      const cat = CATEGORIES.find((c) => c.id === category);
      if (!cat || !cat.match(q.guru)) return false;
    }
    if (search) {
      const byText =
        q.quote.includes(search) ||
        q.guru.toLowerCase().includes(search.toLowerCase()) ||
        q.place.includes(search);
      if (!byText) return false;
    }
    if (serialSearch.trim()) {
      if (!String(q.number).includes(serialSearch.trim())) return false;
    }
    return true;
  });

  const selectedCategoryTotal = useMemo(() => {
    if (!selected) return 0;

    if (!category) {
      return CATEGORIES.reduce(
        (total, cat) =>
          total + quotesData.filter((q) => cat.match(q.guru)).length,
        0,
      );
    }

    const activeCategory = CATEGORIES.find((cat) => cat.id === category);
    if (!activeCategory) return 0;
    return quotesData.filter((q) => activeCategory.match(q.guru)).length;
  }, [category, selected]);

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    return filtered.findIndex((q) => q.number === selected.number);
  }, [filtered, selected]);

  const goToRelativeQuote = useCallback(
    (delta) => {
      if (!selected || selectedIndex < 0) return;
      const targetIndex = selectedIndex + delta;
      if (targetIndex < 0 || targetIndex >= filtered.length) return;
      const target = filtered[targetIndex];
      if (target) loadQuote(target);
    },
    [filtered, selected, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = (event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (!selected) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeQuote(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeQuote(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativeQuote, selected]);

  const openLastReadQuote = () => {
    if (!lastReadQuote) return;
    const quote = quotesData.find(
      (q) => String(q.number) === String(lastReadQuote.number),
    );
    if (quote) loadQuote(quote);
  };

  const contentHighlightWord =
    wordResults !== null ? (resultHighlightWord || wordQuery).trim() : "";

  return (
    <main
      className={`page-content vachanamrut-page swamini-vato-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          Brahm Ratna
          <span className="reader-muted" style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}>
            ({filtered.length})
          </span>
        </h1>
        <p className="header-subtitle">બ્રહ્મ રત્ન · સુવચન</p>
        {lastReadQuote && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastReadQuote}
            title="Open last read quote"
          >
            Last Read Quote: #{lastReadQuote.number}
          </button>
        )}
      </section>

      {/* ── Full-text word search ── */}
      <form className="word-search-bar" onSubmit={handleWordSearch}>
        <input
          className="word-search-input"
          type="text"
          placeholder="Search word in all quotes… (Gujarati or English)"
          value={wordQuery}
          onChange={(e) => setWordQuery(e.target.value)}
        />
        <button type="submit" className="word-search-btn">
          Search
        </button>
        <button
          type="button"
          className="word-search-clear"
          onClick={() => setDarkMode((d) => !d)}
          title="Toggle light/dark mode"
        >
          {darkMode ? "Light" : "Dark"}
        </button>
        {wordResults !== null && (
          <button
            type="button"
            className="word-search-clear"
            onClick={clearWordSearch}
          >
            ✕ Clear
          </button>
        )}
      </form>

      {/* ── Word search results ── */}
      {wordResults !== null && (
        <div className="word-results-panel">
          {wordResults.length === 0 ? (
            <p className="word-results-empty">
              No results found for "{wordQuery}".
            </p>
          ) : (
            <>
              <p className="word-results-count">
                {wordResults.length} result{wordResults.length !== 1 ? "s" : ""}{" "}
                for "{wordQuery}"
              </p>
              <ul className="word-results-list brahm-ratna-results-list">
                {wordResults.map(({ item, snippet, hit }) => (
                  <li
                    key={item.number}
                    className={`word-result-item${selected?.number === item.number ? " active" : ""}`}
                    onClick={() => handleResultClick(item)}
                  >
                    <div className="word-result-header">
                      <span className="vachan-name">#{item.number}</span>
                      <span className="vachan-name-guj">{item.guru}</span>
                    </div>
                    <p className="word-result-snippet">
                      <Highlighted
                        text={snippet}
                        word={hit || resultHighlightWord}
                      />
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="vachanamrut-layout">
        {/* Category filter */}
        <div className="vachanamrut-chapters" role="group" aria-label="Categories">
          <button
            className={`chapter-btn${!category ? " active" : ""}`}
            aria-pressed={!category}
            onClick={() => {
              setCategory(null);
              setSelected(null);
            }}
          >
            All
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`chapter-btn${category === cat.id ? " active" : ""}`}
              aria-pressed={category === cat.id}
              onClick={() => {
                setCategory(cat.id);
                setSelected(null);
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* List + Content */}
        <div className="vachanamrut-body">
          <aside className="vachanamrut-list-panel">
            <input
              className="vachanamrut-search"
              type="text"
              placeholder="Filter list…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="vachanamrut-search vachanamrut-search-serial"
              type="text"
              placeholder="Serial no search (e.g. 12)"
              value={serialSearch}
              onChange={(e) => setSerialSearch(e.target.value)}
            />
            <ul className="vachanamrut-list">
              {filtered.map((q) => (
                <li
                  key={q.number}
                  className={`vachanamrut-item${selected?.number === q.number ? " active" : ""}`}
                  aria-current={selected?.number === q.number ? "true" : undefined}
                  onClick={() => loadQuote(q)}
                >
                  <span className="vachan-name">#{q.number}</span>
                  <span className="vachan-name-guj">
                    {q.quote.slice(0, 20)}
                    {q.quote.length > 20 ? "…" : ""}
                  </span>
                </li>
              ))}
            </ul>
          </aside>

          <section className="vachanamrut-content-panel">
            {!selected ? (
              <div className="vachanamrut-placeholder">
                <p>સુવચન વાંચવા માટે ડાબી બાજુથી પસંદ કરો.</p>
                <p>Select a quote from the list to read.</p>
              </div>
            ) : (
              <article className="vachanamrut-article brahm-ratna-card">
                <div className="font-resize-bar">
                  <button
                    onClick={() => goToRelativeQuote(-1)}
                    disabled={selectedIndex <= 0}
                    title="Previous quote (Left Arrow)"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => goToRelativeQuote(1)}
                    disabled={
                      selectedIndex === -1 ||
                      selectedIndex >= filtered.length - 1
                    }
                    title="Next quote (Right Arrow)"
                  >
                    Next →
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.max(s - 0.15, 0.7))}
                  >
                    A-
                  </button>
                  <button
                    onClick={() => setFontSize((s) => Math.min(s + 0.15, 2.5))}
                  >
                    A+
                  </button>
                  <span>{Math.round(fontSize * 100)}%</span>
                </div>
                {selected && (
                  <div className="vachan-meta">
                    <span>Quote {selected.number}</span>
                    <span>
                      {selectedCategoryTotal
                        ? `out of ${selectedCategoryTotal}`
                        : ""}
                    </span>
                  </div>
                )}
                <blockquote className="brahm-ratna-quote">
                  <div style={{ fontSize: `${fontSize}em` }}>
                    <Highlighted
                      text={selected.quote || ""}
                      word={contentHighlightWord}
                    />
                  </div>
                </blockquote>
                <div className="brahm-ratna-meta">
                  {selected.guru && (
                    <div className="brahm-ratna-guru">
                      <Highlighted
                        text={selected.guru || ""}
                        word={contentHighlightWord}
                      />
                    </div>
                  )}
                  {selected.date && (
                    <div className="brahm-ratna-detail">
                      <span className="brahm-ratna-label">તારીખ:</span>{" "}
                      <Highlighted
                        text={selected.date || ""}
                        word={contentHighlightWord}
                      />
                    </div>
                  )}
                  {selected.place && (
                    <div className="brahm-ratna-detail">
                      <span className="brahm-ratna-label">સ્થળ:</span>{" "}
                      <Highlighted
                        text={selected.place || ""}
                        word={contentHighlightWord}
                      />
                    </div>
                  )}
                </div>
              </article>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
